// Pure GS1 Digital Link / element-string parser. Browser- and SSR-safe.
// Not affiliated with or endorsed by GS1. This tool performs structural
// parsing only — it does not perform official GS1 verification.

export type AiKey =
  | "01" // GTIN
  | "10" // batch/lot
  | "11" // production date
  | "13" // packaging date
  | "15" // best-before
  | "17" // expiry
  | "21" // serial
  | "22" // CPV (variant)
  | "240" // additional product id
  | "30" // count
  | "37" // count of items
  | "00" // SSCC
  | "414" // GLN
  | "8003" // GRAI
  | "8004" // GIAI
  | "8006" // ITIP
  | "8010" // CPID
  | "8017" // GSRN provider
  | "8018"; // GSRN recipient

export type AiSpec = {
  ai: string;
  label: string;
  shortName?: string; // Digital Link convenience name e.g. "gtin"
  fixedLength?: number;
  /** Render the raw value as something user-friendly. */
  format?: (raw: string) => string;
  /** Short plain-language explanation. */
  help: string;
};

function fmtDate6(raw: string): string {
  // YYMMDD -> YYYY-MM-DD. Century per the GS1 General Specifications
  // (section 7.12): a year more than 50 years ahead of the current year is
  // in the previous century, one more than 49 years behind it in the next.
  if (!/^\d{6}$/.test(raw)) return raw;
  const yy = Number(raw.slice(0, 2));
  const now = new Date().getFullYear();
  const diff = yy - (now % 100);
  const century = Math.floor(now / 100) * 100 + (diff >= 51 ? -100 : diff <= -50 ? 100 : 0);
  const yyyy = century + yy;
  const mm = raw.slice(2, 4);
  let dd = raw.slice(4, 6);
  if (dd === "00") dd = "—"; // last day of month indicator
  return `${yyyy}-${mm}-${dd}`;
}

export const AI_SPECS: Record<string, AiSpec> = {
  "00": {
    ai: "00",
    label: "SSCC",
    shortName: "sscc",
    fixedLength: 18,
    help: "Serial Shipping Container Code.",
  },
  "01": {
    ai: "01",
    label: "GTIN",
    shortName: "gtin",
    fixedLength: 14,
    help: "Global Trade Item Number for the product.",
  },
  "10": {
    ai: "10",
    label: "Batch / Lot",
    shortName: "lot",
    help: "Manufacturing batch or lot number (variable, ≤20 chars).",
  },
  "11": {
    ai: "11",
    label: "Production date",
    fixedLength: 6,
    format: fmtDate6,
    help: "When the item was produced.",
  },
  "13": {
    ai: "13",
    label: "Packaging date",
    fixedLength: 6,
    format: fmtDate6,
    help: "When the item was packed.",
  },
  "15": {
    ai: "15",
    label: "Best before",
    fixedLength: 6,
    format: fmtDate6,
    help: "Best-before date for the item.",
  },
  "17": {
    ai: "17",
    label: "Expiry",
    shortName: "expiry",
    fixedLength: 6,
    format: fmtDate6,
    help: "Use-by / expiration date.",
  },
  "21": {
    ai: "21",
    label: "Serial number",
    shortName: "ser",
    help: "Item-level serial number (variable, ≤20 chars).",
  },
  "22": {
    ai: "22",
    label: "CPV (consumer product variant)",
    shortName: "cpv",
    help: "Variant identifier such as colour or size.",
  },
  "240": {
    ai: "240",
    label: "Additional product id",
    help: "Manufacturer's additional identifier.",
  },
  "30": { ai: "30", label: "Count of items", help: "Variable-count of items." },
  "37": {
    ai: "37",
    label: "Count of trade items",
    help: "Number of trade items contained in a logistic unit.",
  },
  "414": {
    ai: "414",
    label: "GLN",
    shortName: "gln",
    fixedLength: 13,
    help: "Global Location Number.",
  },
  "8003": { ai: "8003", label: "GRAI", help: "Global Returnable Asset Identifier." },
  "8004": { ai: "8004", label: "GIAI", help: "Global Individual Asset Identifier." },
  "8006": {
    ai: "8006",
    label: "ITIP",
    fixedLength: 18,
    help: "Identification of an Individual Trade Item Piece.",
  },
  "8010": { ai: "8010", label: "CPID", help: "Component / Part Identifier." },
  "8017": {
    ai: "8017",
    label: "GSRN (provider)",
    fixedLength: 18,
    help: "Global Service Relation Number — service provider.",
  },
  "8018": {
    ai: "8018",
    label: "GSRN (recipient)",
    fixedLength: 18,
    help: "Global Service Relation Number — service recipient.",
  },
};

// shortName -> AI lookup for Digital Link path segments.
const SHORTNAME_TO_AI: Record<string, string> = Object.fromEntries(
  Object.values(AI_SPECS)
    .filter((s) => s.shortName)
    .map((s) => [s.shortName!, s.ai]),
);

export type ParsedElement = {
  ai: string;
  label: string;
  raw: string;
  formatted: string;
  help: string;
};

export type ParseResult = {
  ok: boolean;
  /** "digital-link" (URL) | "element-string" (e.g. (01)…(10)…) | "raw-gs1" (FNC1-style) | "empty" | "unknown" */
  shape: "digital-link" | "element-string" | "raw-gs1" | "empty" | "unknown";
  primary?: ParsedElement;
  elements: ParsedElement[];
  /** Query/path fragments we couldn't classify. */
  extras: { key: string; value: string }[];
  /** Original input (trimmed). */
  input: string;
  reason?: string;
};

function buildElement(ai: string, raw: string): ParsedElement {
  const spec = AI_SPECS[ai];
  if (!spec) {
    return {
      ai,
      label: `AI (${ai})`,
      raw,
      formatted: raw,
      help: "Unrecognised Application Identifier — passed through as-is.",
    };
  }
  return {
    ai,
    label: spec.label,
    raw,
    formatted: spec.format ? spec.format(raw) : raw,
    help: spec.help,
  };
}

/** Parse a GS1 element string like (01)07640156790000(10)ABC123(17)260131(21)XYZ. */
export function parseElementString(input: string): ParseResult {
  const elements: ParsedElement[] = [];
  const re = /\((\d{2,4})\)([^()]*)/g;
  let m: RegExpExecArray | null;
  let saw = false;
  while ((m = re.exec(input)) !== null) {
    saw = true;
    elements.push(buildElement(m[1], m[2].trim()));
  }
  if (!saw) {
    return {
      ok: false,
      shape: "unknown",
      elements: [],
      extras: [],
      input,
      reason: "No (AI)value groups found.",
    };
  }
  return {
    ok: true,
    shape: "element-string",
    primary: elements.find((e) => e.ai === "01" || e.ai === "00") ?? elements[0],
    elements,
    extras: [],
    input,
  };
}

/** Parse a GS1 Digital Link URL like https://id.gs1.org/01/07640156790000/10/ABC123?17=260131&21=XYZ */
export function parseDigitalLink(input: string): ParseResult {
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return {
      ok: false,
      shape: "unknown",
      elements: [],
      extras: [],
      input,
      reason: "Not a valid URL.",
    };
  }
  const segments = url.pathname.split("/").filter(Boolean);
  const elements: ParsedElement[] = [];
  const extras: { key: string; value: string }[] = [];

  // The standard allows a custom path before the primary key
  // (https://brand.example/p/01/...): start at the first primary-key AI.
  const PRIMARY_KEYS = ["00", "01", "253", "255", "401", "402", "414", "417", "8003", "8004", "8006", "8010", "8013", "8017", "8018"];
  const start = Math.max(
    0,
    segments.findIndex((seg, i) => {
      const k = decodeURIComponent(seg);
      const ai = /^\d+$/.test(k) ? k : SHORTNAME_TO_AI[k];
      return !!ai && PRIMARY_KEYS.includes(ai) && segments[i + 1] !== undefined;
    }),
  );

  // Walk the path as key/value pairs. Keys may be AIs or shortNames.
  for (let i = start; i < segments.length; i += 2) {
    const key = decodeURIComponent(segments[i]);
    const value = segments[i + 1] !== undefined ? decodeURIComponent(segments[i + 1]) : "";
    const ai = /^\d+$/.test(key) ? key : SHORTNAME_TO_AI[key];
    if (ai && AI_SPECS[ai]) {
      elements.push(buildElement(ai, value));
    } else if (value === "" && key) {
      extras.push({ key, value: "" });
    } else {
      extras.push({ key, value });
    }
  }

  // Query string: AI numbers or shortNames.
  for (const [key, value] of url.searchParams.entries()) {
    const ai = /^\d+$/.test(key) ? key : SHORTNAME_TO_AI[key];
    if (ai && AI_SPECS[ai]) {
      elements.push(buildElement(ai, value));
    } else {
      extras.push({ key, value });
    }
  }

  if (elements.length === 0) {
    return {
      ok: false,
      shape: "digital-link",
      elements: [],
      extras,
      input,
      reason: "URL parsed but no recognised GS1 Application Identifiers found.",
    };
  }

  return {
    ok: true,
    shape: "digital-link",
    primary: elements.find((e) => e.ai === "01" || e.ai === "00") ?? elements[0],
    elements,
    extras,
    input,
  };
}

/**
 * Parse a raw GS1 element string with no parentheses, where variable-length
 * AIs are separated by GS (ASCII 29) or the printable substitute "{GS}" / "^".
 */
export function parseRawGs1(input: string): ParseResult {
  const GS = "\u001D";
  const cleaned = input.replace(/\{GS\}/gi, GS).replace(/\^/g, GS);
  const elements: ParsedElement[] = [];
  let i = 0;
  while (i < cleaned.length) {
    // Skip stray GS / FNC1
    if (cleaned[i] === GS) {
      i++;
      continue;
    }
    // AI is 2, 3 or 4 digits — try longest match against AI_SPECS.
    let ai: string | null = null;
    for (const len of [4, 3, 2]) {
      const candidate = cleaned.slice(i, i + len);
      if (AI_SPECS[candidate]) {
        ai = candidate;
        break;
      }
    }
    if (!ai) {
      return {
        ok: false,
        shape: "raw-gs1",
        elements,
        extras: [{ key: "remainder", value: cleaned.slice(i) }],
        input,
        reason: `Couldn't recognise an AI starting at position ${i}.`,
      };
    }
    const spec = AI_SPECS[ai];
    i += ai.length;
    let raw: string;
    if (spec.fixedLength !== undefined) {
      raw = cleaned.slice(i, i + spec.fixedLength);
      i += spec.fixedLength;
    } else {
      const next = cleaned.indexOf(GS, i);
      const end = next === -1 ? cleaned.length : next;
      raw = cleaned.slice(i, end);
      i = end;
    }
    elements.push(buildElement(ai, raw));
  }
  if (elements.length === 0) {
    return { ok: false, shape: "unknown", elements: [], extras: [], input, reason: "Empty input." };
  }
  return {
    ok: true,
    shape: "raw-gs1",
    primary: elements.find((e) => e.ai === "01" || e.ai === "00") ?? elements[0],
    elements,
    extras: [],
    input,
  };
}

/** Auto-detect the input shape and parse. */
export function parseGs1(input: string): ParseResult {
  const trimmed = input.trim();
  if (!trimmed) {
    return {
      ok: false,
      shape: "empty",
      elements: [],
      extras: [],
      input: trimmed,
      reason: "Paste a GS1 Digital Link URL or element string.",
    };
  }
  if (/^https?:\/\//i.test(trimmed)) return parseDigitalLink(trimmed);
  if (/^\s*\(\d{2,4}\)/.test(trimmed)) return parseElementString(trimmed);
  return parseRawGs1(trimmed);
}
